import { useState, useRef, useEffect } from 'react';
import { useSocket } from '../contexts/SocketContext';
import { MESSAGE_STATUS, MSG_USER_ROLE } from '../utils/messageHelpers';
import { SOCKET_EVENT_NAME } from '../constants/constants';

const SCROLL_THRESHOLD = 75;

const initialChatState = {
  activeChatId: '',
  allChats: [],
  filteredChats: [],
  searchQuery: '',
  currentMessages: [],
  activeChatInfo: {},
  newMessage: '',
  currentPage: 1,
  canLoadMore: true,
  isLoading: false,
  unreadCounts: {}
};

export const useChat = (getChatList) => {
  const [chatState, setChatState] = useState(initialChatState);
  const [isPageLoading, setIsPageLoading] = useState(true);
  const isLoadingRef = useRef(false);
  const { emit, on, isConnected } = useSocket();

  const moveUserToTopHelper = (allChats, userId, searchQuery) => {
    const userIndex = allChats.findIndex((chat) => chat.id === userId);
    if (userIndex <= 0)
      return {
        allChats,
        filteredChats: searchQuery
          ? allChats.filter((chat) =>
              chat.candidate_name
                .toLowerCase()
                .replace('  ', ' ')
                .includes(searchQuery.toLowerCase().replace('  ', ' '))
            )
          : allChats
      };

    const updatedChats = [...allChats];
    const [user] = updatedChats.splice(userIndex, 1);
    updatedChats.unshift(user);

    return {
      allChats: updatedChats,
      filteredChats: searchQuery
        ? updatedChats.filter((chat) =>
            chat.candidate_name
              .toLowerCase()
              .replace('  ', ' ')
              .includes(searchQuery.toLowerCase().replace('  ', ' '))
          )
        : updatedChats
    };
  };

  const moveUserToTop = (userId) => {
    setChatState((prev) => {
      const { allChats, filteredChats } = moveUserToTopHelper(
        prev.allChats,
        userId,
        prev.searchQuery
      );
      return { ...prev, allChats, filteredChats };
    });
  };

  const loadChatList = async (isDisplayLoader = true) => {
    const chatData = (await getChatList(isDisplayLoader))?.data;
    setIsPageLoading(false);
    if (chatData) {
      const unreadCounts = {};
      const userListIds = chatData.map((chat) => {
        unreadCounts[chat.id] = chat.messageCount;
        return chat.id;
      });
      setChatState((prev) => ({
        ...prev,
        allChats: chatData,
        filteredChats: chatData,
        unreadCounts: { ...prev.unreadCounts, ...unreadCounts }
      }));

      if (userListIds.length) {
        emit(
          SOCKET_EVENT_NAME.GET_USER_STATUS,
          { ids: userListIds, userType: MSG_USER_ROLE.CANDIDATE },
          (data) => {
            if (data?.success) {
              const user_status = data?.data ?? {};
              if (typeof user_status !== 'object' || Object.keys(user_status).length === 0) return;
              setChatState((prev) => ({
                ...prev,
                onlineUsers: { ...prev.onlineUsers, ...user_status }
              }));
            }
          }
        );
      }
    }
  };

  const updateMessageStatus = (updates) => {
    const updateMsgId = updates?.tempId || updates.id;
    setChatState((prev) => ({
      ...prev,
      currentMessages: prev.currentMessages.map((msg) =>
        msg.tempId === updateMsgId ? { ...msg, status: updates.status, id: updates.id } : msg
      )
    }));
  };

  const handleNewMessage = (data) => {
    setChatState((prev) => {
      if (data.senderId.toString() === prev.activeChatId.toString()) {
        const { allChats, filteredChats } = moveUserToTopHelper(
          prev.allChats,
          data.senderId,
          prev.searchQuery
        );
        return {
          ...prev,
          allChats,
          filteredChats,
          currentMessages: [...prev.currentMessages, data]
        };
      } else {
        const isInList = prev.allChats.some(
          (chat) => chat.id.toString() === data.senderId.toString()
        );
        if (!isInList) {
          loadChatList(false);
          return prev;
        }

        const { allChats, filteredChats } = moveUserToTopHelper(
          prev.allChats,
          data.senderId,
          prev.searchQuery
        );

        const unreadCounts = {
          ...prev.unreadCounts,
          [data.senderId]: (prev.unreadCounts[data.senderId] || 0) + 1
        };
        return {
          ...prev,
          allChats,
          filteredChats,
          unreadCounts: data?.isMarkAsNotifyCount ? unreadCounts : prev.unreadCounts
        };
      }
    });
  };
  const handleMarkAllMessageAsRead = (data) => {
    if (data.recipientId.toString() === chatState.activeChatId?.toString()) {
      setChatState((prev) => ({
        ...prev,
        currentMessages: prev.currentMessages.map((msg) => ({
          ...msg,
          status: MESSAGE_STATUS.READ
        }))
      }));
    }
  };
  const handleMarkAllMessageAsDelivered = (data) => {
    if (data.recipientId.toString() === chatState.activeChatId.toString()) {
      setChatState((prev) => ({
        ...prev,
        currentMessages: prev.currentMessages.map((msg) => ({
          ...msg,
          status: MESSAGE_STATUS.DELIVERED
        }))
      }));
    }
  };

  const handleUserStatus = (data) => {
    setChatState((prev) => ({
      ...prev,
      onlineUsers: { ...prev.onlineUsers, [data.userId]: data.status }
    }));
  };
  const handleUpdateChatUnreadCount = (data) => {
    setChatState((prev) => ({
      ...prev,
      unreadCounts: {
        ...prev.unreadCounts,
        [data.senderId]: Math.max(prev.unreadCounts[data.senderId] - data.decrementBy, 0)
      }
    }));
  };
  const handleUserDelete = (data) => {
    setChatState((prev) => {
      const allChats = prev.allChats.filter((chat) => chat?.id?.toString() !== data?.toString());
      return {
        ...prev,
        activeChatId: prev.activeChatId.toString() === data?.toString() ? '' : prev.activeChatId,
        allChats,
        filteredChats: prev.searchQuery
          ? prev.filteredChats.filter((chat) => chat?.id?.toString() !== data?.toString())
          : allChats
      };
    });
  };
  const handleUserAdd = () => {
    loadChatList(false);
  };

  useEffect(() => {
    if (isConnected && !chatState.allChats.length) loadChatList();
    if (isConnected && chatState.activeChatId) {
      setTimeout(() => {
        emit(SOCKET_EVENT_NAME.CHAT_OPEN, chatState.activeChatId);
      }, 100);
    }
    return () => emit(SOCKET_EVENT_NAME.CHAT_CLOSE);
  }, [isConnected]);

  useEffect(() => {
    if (!isConnected) return;

    const unsubscribeNewMessage = on(SOCKET_EVENT_NAME.NEW_MESSAGE, handleNewMessage);
    const unsubscribeMessageStatus = on(
      SOCKET_EVENT_NAME.MESSAGE_STATUS_UPDATE,
      updateMessageStatus
    );
    // const unsubscribeUnreadCounts = on('unread_counts', handleUnreadCounts);

    const unsubscribeAllMessagesRead = on(
      SOCKET_EVENT_NAME.ALL_MESSAGE_READ,
      handleMarkAllMessageAsRead
    );
    const unsubscribeUserStatus = on(SOCKET_EVENT_NAME.USER_STATUS, handleUserStatus);
    const unsubscribeAllMessagesDelivered = on(
      SOCKET_EVENT_NAME.ALL_MESSAGE_DELIVERED,
      handleMarkAllMessageAsDelivered
    );
    const unsubscribeChatUnreadCount = on(
      SOCKET_EVENT_NAME.CHAT_UNREAD_MSG_COUNT,
      handleUpdateChatUnreadCount
    );
    const unsubscribeUserDelete = on(SOCKET_EVENT_NAME.USER_DELETE, handleUserDelete);
    const unsubscribeUserAdd = on(SOCKET_EVENT_NAME.USER_ADD, handleUserAdd);

    return () => {
      unsubscribeNewMessage?.();
      // unsubscribeUnreadCounts?.();
      unsubscribeMessageStatus?.();
      unsubscribeUserStatus?.();
      unsubscribeAllMessagesDelivered?.();
      unsubscribeAllMessagesRead?.();
      unsubscribeChatUnreadCount?.();
      unsubscribeUserDelete?.();
      unsubscribeUserAdd?.();
    };
  }, [isConnected, chatState.activeChatId]);

  return {
    chatState,
    setChatState,
    moveUserToTop,
    isLoadingRef,
    isPageLoading,
    emit,
    SCROLL_THRESHOLD
  };
};
